import { Box, Collapse, Group, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronRight } from '@tabler/icons-react';
import { getAppEnv } from '~portal/env';
import { formatElapsedSeconds } from './elapsed-format';
import { MdContent } from './MdContent';
import * as styles from './ThinkingDisplay.css';

type ThinkingDisplayProps = {
  text: string;
  /** Approximate thinking duration in ms, rendered as "Thought for Ns". */
  tElapsed?: number;
  /** Stream in progress — shows "Thinking…" and starts expanded. */
  isActive?: boolean;
};

const formatElapsed = (ms?: number): string => {
  if (!ms) return '';
  if (ms < 1000) return ` for ${ms}ms`;
  return ` for ${formatElapsedSeconds(Math.round(ms / 1000))}`;
};

/**
 * @desc Collapsible reasoning disclosure ("Thought for Ns"). Auto-expands while the
 * model is actively reasoning, then collapses to a one-line summary the user can
 * re-open. Reasoning renders dimmed/italic behind a left rail to de-emphasise it
 * relative to the final answer.
 *
 * `VITE_RENDER_MODEL_THINKING=false` withholds the whole disclosure, not just its expanded
 * body: the collapsed "Thought for Ns" row is itself model reasoning surfaced to the user, so
 * leaving a column of them behind would not be "thinking hidden". Tool and skill rows are a
 * different fact and stay — they answer to `VITE_RENDER_TASK_DETAILS`. Gating here rather than
 * at the call site keeps one seam: every caller of this component is a reasoning disclosure.
 */
export function ThinkingDisplay({ text, tElapsed, isActive = false }: ThinkingDisplayProps) {
  const [opened, { toggle }] = useDisclosure(isActive);

  if (!getAppEnv().render.modelThinking) return null;
  if (!text) return null;

  const label = isActive ? 'Thinking…' : `Thought${formatElapsed(tElapsed)}`;

  return (
    <Box py={4}>
      <UnstyledButton onClick={toggle}>
        <Group gap={4} wrap="nowrap">
          <IconChevronRight
            size={14}
            color="var(--mantine-color-dimmed)"
            style={{
              transform: opened ? 'rotate(90deg)' : 'none',
              transition: 'transform 150ms ease',
            }}
          />
          <Text size="xs" c="dimmed" fw={500}>
            {label}
          </Text>
        </Group>
      </UnstyledButton>

      <Collapse in={opened}>
        <Box pl="sm" mt={4} className={styles.transcript}>
          <Text size="sm" c="dimmed" fs="italic" component="div">
            <MdContent text={text} />
          </Text>
        </Box>
      </Collapse>
    </Box>
  );
}
